"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.APIManager = exports.RequestError = exports.HTTP = void 0;
const fetch_1 = require("./fetch");
const query_string_1 = __importDefault(require("query-string"));
/**
 * Helper class for abstracting URL manipulation specifically for
 * API endpoints.
 *
 */
class HTTP {
    /**
     * Constructor for `HTTP` helper class.
     *
     * `apiEndpoints` example:
     * ```javascript
     *	{
     *		getBalance: "/zilliqa/addresses/:address",
     *		listTransactions: "/zilliqa/addresses/:address/txs",
     *	};
     * ```
     *
     * @param apiPrefix prefix to add for all endpoints URL construction.
     * @param apiEndpoints see `apiEndpoints` example above.
     */
    constructor(apiPrefix, apiEndpoints, opts) {
        var _a;
        /**
         * Path generator to obtain URL for a specific endpoint
         * provided in the constructor.
         *
         * example usage:
         * ```javascript
         * const http = new HTTP("http://localhost/api", { getUser: "/users/:user_id/detail" });
         * const url = http.path("getUser", { user_id: 42 }, { access_token: "awesomeAccessToken" });
         * // url: http://localhost/api/users/42/detail?access_token=awesomeAccessToken
         * ```
         *
         * @param path a key of apiEndpoints provided in the constructor.
         * @param routeParams object map for route parameters.
         * @param queryParams object map for query parameters.
         */
        this.path = (path, routeParams, queryParams) => {
            let url = `${this.apiPrefix}${this.apiEndpoints[path]}`;
            if (this.debugMode) {
                this.debugLog("path", url);
            }
            // substitute route params
            if (routeParams) {
                for (const paramKey in routeParams) {
                    url = url.replace(`:${paramKey}`, routeParams[paramKey]);
                }
            }
            // append query params
            if (queryParams) {
                url += `?${query_string_1.default.stringify(queryParams)}`;
            }
            return url;
        };
        /**
         * Executes HTTP GET request with fetch
         */
        this.get = ({ url, headers }) => {
            return (0, fetch_1.fetch)(url, {
                method: "GET",
                headers,
            });
        };
        /**
         * Executes HTTP POST request with fetch
         */
        this.post = (options) => {
            return (0, fetch_1.fetch)(options.url, {
                method: "POST",
                headers: Object.assign({ "Content-Type": options.content_type || "application/json" }, options.headers),
                body: JSON.stringify(options.body),
            });
        };
        /**
         * Executes HTTP DELETE request with fetch
         */
        this.del = (options) => {
            return (0, fetch_1.fetch)(options.url, {
                method: "DELETE",
                headers: Object.assign({ "Content-Type": options.content_type || "application/json" }, options.headers),
                body: JSON.stringify(options.body),
            });
        };
        /**
         * Executes HTTP multipart POST request with fetch
         */
        this.raw = (options) => {
            const { url } = options, otherOpts = __rest(options, ["url"]);
            return (0, fetch_1.fetch)(url, otherOpts);
        };
        this.apiPrefix = apiPrefix;
        this.apiEndpoints = apiEndpoints;
        this.debugMode = (_a = opts === null || opts === void 0 ? void 0 : opts.debugMode) !== null && _a !== void 0 ? _a : false;
    }
    /**
     * A helper message to log methods when they are called
     * HTTP#debugMode must be set to true to turn on logging.
     * @param {any[]} args - items to be logged
     */
    debugLog(...args) {
        if (!this.debugMode)
            return;
        console.log(...args);
    }
}
exports.HTTP = HTTP;
class RequestError extends Error {
    constructor(result, message) {
        super(message);
        this.result = result;
        this.message = message || result.statusText;
    }
}
exports.RequestError = RequestError;
const defaultResponseParser = (response) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const { status, statusText, headers, url } = response;
    const result = { status, statusText, headers, url };
    try {
        const responseJson = yield response.json();
        result.data = responseJson;
    }
    catch (e) {
        // empty catch
    }
    if (response.status >= 400 && response.status < 600) {
        throw new RequestError(result, ((_a = result.data) === null || _a === void 0 ? void 0 : _a.error) || "unknown error");
    }
    return result;
});
class APIManager {
    constructor(apiPrefix, endpoints, responseParser = defaultResponseParser) {
        this.apiPrefix = apiPrefix;
        this.endpoints = endpoints;
        this.responseParser = responseParser;
        this.createExecutor = (url) => ({
            get: (options = {}, parser = this.responseParser) => __awaiter(this, void 0, void 0, function* () { return this.http.get(Object.assign({ url }, options)).then(parser); }),
            post: (options = {}, parser = this.responseParser) => __awaiter(this, void 0, void 0, function* () { return this.http.post(Object.assign({ url }, options)).then(parser); }),
            delete: (options = {}, parser = this.responseParser) => __awaiter(this, void 0, void 0, function* () { return this.http.del(Object.assign({ url }, options)).then(parser); }),
            raw: (options = {}, parser = this.responseParser) => __awaiter(this, void 0, void 0, function* () { return this.http.raw(Object.assign({ url }, options)).then(parser); }),
        });
        this.http = new HTTP(apiPrefix, endpoints);
    }
    path(path, routeParams, queryParams) {
        const url = this.http.path(path, routeParams, queryParams);
        return this.createExecutor(url);
    }
}
exports.APIManager = APIManager;
